import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ExportListViewData, ExportQuery } from '@y42/data-export';
import { AllDatasourcesQuery, DatasourceListViewData } from '@y42/datasource';
import { datasourceTableIcon, exportTriggerIcon } from '@y42/feature/icons';
import { DatasourceTypeEnum } from '@y42/json-schema';
import { SqlModelingQuery } from '@y42/sql-model';
import { UiModelingQuery } from '@y42/ui-model';
import { isEqual } from 'lodash/fp';
import { combineLatest, debounceTime, distinctUntilChanged, filter, firstValueFrom, map, switchMap, tap } from 'rxjs';
// TODO: those utils should be in modeling facade
import { findSqlTableInputs } from '../../../../modeling/creators/sql-modeling-creator/utils/find-sql-model-table-inputs';
import { findInputTables } from '../../../../modeling/creators/ui-modeling-creator/utils/find-model-table-inputs';
import {
  OrchestrationDerivedNode,
  OrchestrationDerivedNodeDictionary,
  OrchestrationNodeType,
} from '../models/orchestration-diagram.model';

@UntilDestroy()
@Injectable()
export class OrchestrationDiagramHydrationService {
  constructor(
    private readonly allDatasourcesQuery: AllDatasourcesQuery,
    private readonly uiModelingQuery: UiModelingQuery,
    private readonly sqlModelingQuery: SqlModelingQuery,
    private readonly exportQuery: ExportQuery,
  ) {}

  private _orchestrationDerivedNodesDictionary: OrchestrationDerivedNodeDictionary | null = null;

  get orchestrationDerivedNodesDictionary() {
    return this._orchestrationDerivedNodesDictionary;
  }

  readonly orchestrationDerivedNodeDictionary$ = combineLatest([
    this.allDatasourcesQuery.loading$,
    this.exportQuery.loading$,
  ]).pipe(
    map((loadings) => loadings.every((l) => !l)),
    filter((allLoaded) => !!allLoaded),
    switchMap(() => combineLatest([this.allDatasourcesQuery.listItems$, this.exportQuery.listItems$])),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    map(([datasources, exports]) => this.calculateDerivedDictionary(datasources, exports)),
    tap((dictionary) => (this._orchestrationDerivedNodesDictionary = dictionary)),
  );

  async getEntityInputsMap(): Promise<Record<string, string[]>> {
    return firstValueFrom(
      combineLatest([this.uiModelingQuery.loading$, this.sqlModelingQuery.loading$, this.exportQuery.loading$]).pipe(
        filter((loadings) => loadings.every((l) => !l)),
        switchMap(() =>
          combineLatest([this.uiModelingQuery.items$, this.sqlModelingQuery.items$, this.exportQuery.items$]),
        ),
        debounceTime(500),
        map(async ([uiModels, sqlModels, exports]) => {
          const inputsMap: Record<string, string[]> = {};

          const uiModelsResolved = await Promise.all(uiModels.map((model) => model.getUiModel?.()));
          for (const [index, model] of uiModels.map((value, ind) => [ind, value] as const)) {
            const uiModel = uiModelsResolved[index];
            for (const table of model.tables) {
              if (uiModel) {
                const inputs = findInputTables(table.$name, uiModel);
                inputsMap[table.$path] = inputs;
              }
            }
          }

          const sqlModelsResolved = await Promise.all(sqlModels.map((model) => model.getSqlModel?.()));
          for (const [index, model] of sqlModels.map((value, ind) => [ind, value] as const)) {
            const sqlModel = sqlModelsResolved[index];
            for (const table of model.tables) {
              const tableSql = sqlModel?.find((sm) => sm.table_name === table.$name)?.sql;
              if (tableSql) {
                const inputs = findSqlTableInputs(tableSql);
                inputsMap[table.$path] = inputs;
              }
            }
          }

          const exportTriggers = exports.map((e) => e.triggers).flat();

          for (const trigger of exportTriggers) {
            if (trigger.settings?.datasource_table_path) {
              inputsMap[trigger.$path] = [trigger.settings.datasource_table_path];
            }
          }

          return inputsMap;
        }),
      ),
    );
  }

  private calculateDerivedDictionary(
    datasources: DatasourceListViewData[],
    exports: ExportListViewData[],
  ): OrchestrationDerivedNodeDictionary {
    const dictionary: OrchestrationDerivedNodeDictionary = {};

    datasources.forEach((datasource) => {
      datasource.tables.forEach((table) => {
        const derivedNode: OrchestrationDerivedNode = {
          entityName: datasource.name,
          subEntityName: table.name,
          typeLogo: datasource.typeModel.logo,
          nodeType:
            datasource.typeModel.type === DatasourceTypeEnum.UiModel
              ? OrchestrationNodeType.UiModel
              : datasource.typeModel.type === DatasourceTypeEnum.SqlModel
              ? OrchestrationNodeType.SqlModel
              : OrchestrationNodeType.Integration,
          subEntityLogo: datasourceTableIcon,
          allowIncremental: datasource.typeModel.hasIncremental,
        };

        dictionary[table.$path] = derivedNode;
      });
    });

    exports.forEach((exportItem) => {
      exportItem.triggers.forEach((trigger) => {
        const derivedNode: OrchestrationDerivedNode = {
          entityName: exportItem.name,
          subEntityName: trigger.name,
          typeLogo: exportItem.typeModel.logo,
          nodeType: OrchestrationNodeType.Export,
          subEntityLogo: exportTriggerIcon,
          allowIncremental: exportItem.typeModel.hasIncremental,
        };

        dictionary[trigger.$path] = derivedNode;
      });
    });

    return dictionary;
  }
}
